import React from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, PhoneCall, Phone, Mail, MapPin, Tv, Zap, Star, Wifi } from 'lucide-react';
import WhatsAppButton from './WhatsAppButton';
import { trackPhoneClick } from '../utils/analytics';

const Footer: React.FC = () => {
  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <h3 className="text-xl font-bold mb-4">Easy Vertrag</h3>
            <p className="text-gray-400 mb-4">
              Ihr vertrauenswürdiger Partner für Smartphones, Geräte, Handytarife, Strom und Gas zu den besten Preisen und mit exzellenter Beratung.
            </p>
            <div className="flex space-x-4">
              <a href="https://facebook.com/easyvertrag" target="_blank" rel="noopener noreferrer" className="text-white hover:text-blue-400 transition-colors">
                <span className="sr-only">Facebook</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                </svg>
              </a>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-bold mb-4">Produkte</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/produkte/smartphones"
                  className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
                >
                  <Smartphone className="w-4 h-4" />
                  <span>Smartphones</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/produkte/geraete"
                  className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
                >
                  <Tv className="w-4 h-4" />
                  <span>Geräte</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/produkte/handytarif"
                  className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
                >
                  <PhoneCall className="w-4 h-4" />
                  <span>Handytarif</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/strom-gas"
                  className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
                >
                  <Zap className="w-4 h-4" />
                  <span>Strom & Gas</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/produkte/mobilfunk"
                  className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
                >
                  <Phone className="w-4 h-4" />
                  <span>Mobilfunk</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/internet-zuhause"
                  className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
                >
                  <Wifi className="w-4 h-4" />
                  <span>Internet zuhause</span>
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-bold mb-4">Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                  Startseite
                </Link>
              </li>
              <li>
                <Link to="/handyvertrag-voelklingen" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
                  <PhoneCall className="w-4 h-4" />
                  <span>Handyvertrag Völklingen</span>
                </Link>
              </li>
              <li>
                <Link to="/internet-anbieter-saarland" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
                  <Wifi className="w-4 h-4" />
                  <span>Internet Saarland</span>
                </Link>
              </li>
              <li>
                <Link to="/strom-gas-voelklingen" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  <span>Strom & Gas Völklingen</span>
                </Link>
              </li>
              <li>
                <Link to="/uber-uns" className="text-gray-400 hover:text-white transition-colors">
                  Über uns
                </Link>
              </li>
              <li>
                <Link to="/datenschutz" className="text-gray-400 hover:text-white transition-colors">
                  Datenschutz
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="text-gray-400 hover:text-white transition-colors">
                  Kontakt
                </Link>
              </li>
            </ul>
          </div>
          <div itemScope itemType="http://schema.org/LocalBusiness">
            <h3 className="text-xl font-bold mb-4">Kontakt</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-400" itemProp="address" itemScope itemType="http://schema.org/PostalAddress">
                  <span itemProp="streetAddress">Bismarck Straße 31</span><br />
                  <span itemProp="postalCode">66333</span> <span itemProp="addressLocality">Völklingen</span><br />
                  <span itemProp="addressCountry">Deutschland</span>
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <a href="tel:+491797473491" onClick={() => trackPhoneClick('footer')} className="text-gray-400 hover:text-white transition-colors" itemProp="telephone">
                  +49 179 7473491
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <a href="mailto:easy90az@gmail.com" className="text-gray-400 hover:text-white transition-colors" itemProp="email">
                  easy90az@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <WhatsAppButton 
                  phoneNumber="+49 179 7473491"
                  message="Hallo! Ich interessiere mich für Ihre Angebote von Easy Vertrag."
                  variant="footer"
                  size="small"
                  className="bg-green-600 hover:bg-green-700"
                />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="bg-gray-950 py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm">
              © Copyright 2025 Easy Vertrag. Alle Rechte vorbehalten.
            </p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <Link to="/datenschutz" className="text-gray-500 text-sm hover:text-white transition-colors">
                Datenschutz
              </Link>
              <Link to="/impressum" className="text-gray-500 text-sm hover:text-white transition-colors">
                Impressum
              </Link>
              <Link to="/agb" className="text-gray-500 text-sm hover:text-white transition-colors">
                AGB
              </Link>
              <button
                onClick={() => window.dispatchEvent(new Event('open-cookie-settings'))}
                className="text-gray-500 text-sm hover:text-white transition-colors"
              >
                Cookie-Einstellungen
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;